<template>
  <v-dialog
    v-model="dialog"
    max-width="680"
    scrollable
    persistent
    :z-index="10100"
    content-class="ltcs-hr-request-dialog"
  >
    <div class="ltcs-hr-request-modal">
      <header class="ltcs-hr-request-modal__header">
        <div class="header-title-wrap">
          <v-icon size="20" color="#1e3a5f">mdi-cloud-download-outline</v-icon>
          <h3>롱텀케어 자료요청</h3>
          <span v-if="hasActiveRequest" class="polling-badge">
            <v-progress-circular indeterminate size="12" width="2" color="#1565c0" />
            갱신 중
          </span>
        </div>
        <button type="button" class="close-btn" aria-label="닫기" @click="closeModal">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </header>

      <div class="ltcs-hr-request-modal__body">
        <LoadingOverlay :show="historyLoading && filteredHistoryItems.length === 0" text="불러오는 중..." />

        <div class="request-target-section">
          <div class="section-label">요청 대상</div>
          <div class="ltcs-params-preview">
            <div class="preview-title">
              <v-icon size="14" color="#1e3a5f">mdi-information-outline</v-icon>
              요청 시 전송되는 조건
            </div>
            <div class="preview-chips">
              <span class="preview-chip preview-chip--ym" :class="{ 'is-warn': !selectedYearMonth }">
                <em>조회 연월</em>
                <span class="ym-picker">
                  <button type="button" class="ym-step" aria-label="이전 달" @click="shiftYearMonth(-1)">
                    <v-icon size="16">mdi-chevron-left</v-icon>
                  </button>
                  <label class="ym-display">
                    <strong>{{ formatYearMonth(selectedYearMonth) || '미선택' }}</strong>
                    <input
                      type="month"
                      class="ym-input"
                      :value="yearMonthInputValue"
                      aria-label="조회 연월 선택"
                      @change="onYearMonthInput"
                    />
                  </label>
                  <button type="button" class="ym-step" aria-label="다음 달" @click="shiftYearMonth(1)">
                    <v-icon size="16">mdi-chevron-right</v-icon>
                  </button>
                </span>
                <span v-if="selectedYearMonth" class="ym-code">{{ selectedYearMonth }}</span>
              </span>
              <span class="preview-chip" :class="{ 'is-warn': !bizGubun }">
                <em>사업구분</em>
                <strong>{{ bizGubun || '미선택' }}</strong>
              </span>
            </div>
            <p class="preview-hint">조회 연월은 화살표나 연월을 눌러 바꿀 수 있습니다. 요청 버튼을 누르면 위 값으로 전송하기 전 확인창이 표시됩니다.</p>
          </div>
          <div class="request-cards">
            <div v-for="item in requestTypes" :key="item.type" class="request-card">
              <div class="request-card__icon" :class="`request-card__icon--${item.color}`">
                <v-icon size="18">{{ item.icon }}</v-icon>
              </div>
              <div class="request-card__content">
                <div class="request-card__title">{{ item.name }}</div>
                <div class="request-card__desc">{{ item.description }}</div>
                <div v-if="getCardStatus(item.type)" class="request-card__status">
                  <span class="status-dot" :class="`status-dot--${getCardStatus(item.type)?.color}`"></span>
                  <span>{{ getCardStatus(item.type)?.label }}</span>
                </div>
              </div>
              <button
                type="button"
                class="request-card__btn"
                :disabled="!!loadingMap[item.type]"
                @click="submitRequest(item.type)"
              >
                <v-progress-circular v-if="loadingMap[item.type]" indeterminate size="14" width="2" color="white" />
                <template v-else>요청</template>
              </button>
            </div>
          </div>
        </div>

        <div class="history-section">
          <div class="history-section__header">
            <span class="section-label">요청 내역</span>
            <button type="button" class="refresh-btn" :disabled="historyLoading" @click="loadHistory">
              <v-icon size="14" :class="{ 'mdi-spin': historyLoading }">mdi-refresh</v-icon>
            </button>
          </div>

          <div v-if="filteredHistoryItems.length === 0 && !historyLoading" class="history-empty">
            요청 내역이 없습니다
          </div>

          <div v-else class="history-list">
            <div v-for="item in filteredHistoryItems.slice(0, 15)" :key="item.cmq_idx" class="history-item">
              <div class="history-item__main">
                <span class="history-type">{{ getTypeLabel(item.cmq_type) }}</span>
                <span class="history-params">{{ getParamsLabel(item.cmq_params) }}</span>
              </div>
              <div class="history-item__meta">
                <span class="history-time">{{ formatDateTime(item.cmq_requested_at) }}</span>
                <span class="history-status" :class="`history-status--${item.cmq_status}`">
                  <v-progress-circular
                    v-if="item.cmq_status === 'processing'"
                    indeterminate
                    size="10"
                    width="2"
                    color="#0288d1"
                    class="mr-1"
                  />
                  {{ getStatusLabel(item.cmq_status) }}
                </span>
              </div>
              <div v-if="getLatestProgressMessage(item)" class="history-item__progress">
                {{ formatProgressMessage(getLatestProgressMessage(item)) }}
              </div>
              <div class="history-item__actions">
                <button
                  v-if="item.cmq_status === 'completed' || item.cmq_status === 'failed'"
                  type="button"
                  class="history-action-btn"
                  :class="item.cmq_status === 'completed' ? 'is-success' : 'is-error'"
                  @click="showResultModal(item)"
                >
                  <v-icon size="13">mdi-eye-outline</v-icon>
                  결과
                </button>
                <button
                  v-if="item.cmq_status === 'pending' || item.cmq_status === 'processing'"
                  type="button"
                  class="history-action-btn is-cancel"
                  @click="cancelRequest(item)"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer class="ltcs-hr-request-modal__footer">
        <v-btn variant="text" class="btn-close" @click="closeModal">닫기</v-btn>
      </footer>
    </div>
  </v-dialog>

  <!-- 결과 보기 모달 -->
  <v-dialog v-model="resultModal.show" max-width="2000" scrollable>
    <div class="result-modal result-modal--large">
      <div class="modal-header" :class="resultModal.status">
        <h3>
          <v-icon size="20" class="mr-2">
            {{ resultModal.status === 'completed' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          {{ resultModal.status === 'completed' ? '실행 완료' : '실행 실패' }}
          <span v-if="resultModal.status === 'completed'" class="header-subtitle">
            - {{ getTypeLabel(resultModal.type) }}
          </span>
        </h3>
        <button class="close-btn" @click="closeResultModal">
          <v-icon>mdi-close</v-icon>
        </button>
      </div>
      <div class="modal-body">
        <LoadingOverlay :show="scheduleLoading" text="데이터 불러오는 중..." />

        <div v-if="resultModal.status === 'failed' && resultModal.errorMessage" class="error-section">
          <div class="section-title">
            <v-icon size="16" color="error" class="mr-1">mdi-alert</v-icon>
            실패 원인
          </div>
          <div class="error-message">{{ resultModal.errorMessage }}</div>
        </div>

        <!-- 성공 시: 인건비지출내역 전용 표시 -->
        <div
          v-if="resultModal.status === 'completed' && resultModal.type === 'caion_ltcs_labor_cost'"
          class="labor-cost-section"
        >
          <div v-if="scheduleData.items.length > 0" class="labor-cost-card">
            <div class="labor-cost-title-bar">
              ({{ formatYearMonth(scheduleData.items[0]?.yearMonth) }})
              {{ scheduleData.items[0]?.facilityType || '-' }} 심사결정금액
            </div>
            <table class="labor-cost-table">
              <thead>
                <tr class="header-row-1">
                  <th colspan="2">① 장기요양급여비용(②+③+④)</th>
                  <th colspan="2">② 인건비 비율 반영</th>
                  <th colspan="2">③ 인건비 비율 미반영</th>
                  <th>④ 본인부담금</th>
                </tr>
                <tr class="header-row-2">
                  <th>비용</th>
                  <th>가산</th>
                  <th>비용</th>
                  <th>가산</th>
                  <th>비용</th>
                  <th>가산</th>
                  <th>비용</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="amount">{{ formatNumber(scheduleData.items[0]?.totalCost) }}</td>
                  <td class="amount">{{ formatNumber(scheduleData.items[0]?.totalAdd) }}</td>
                  <td class="amount highlight">{{ formatNumber(scheduleData.items[0]?.laborCost) }}</td>
                  <td class="amount highlight">{{ formatNumber(scheduleData.items[0]?.laborAdd) }}</td>
                  <td class="amount">{{ formatNumber(scheduleData.items[0]?.nonLaborCost) }}</td>
                  <td class="amount">{{ formatNumber(scheduleData.items[0]?.nonLaborAdd) }}</td>
                  <td class="amount">{{ formatNumber(scheduleData.items[0]?.selfPayCost) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="labor-cost-summary">
              <div class="summary-item">
                <span class="label">인건비 반영 합계</span>
                <span class="value">
                  {{ formatNumber((scheduleData.items[0]?.laborCost || 0) + (scheduleData.items[0]?.laborAdd || 0)) }}원
                </span>
              </div>
              <button
                v-if="duplicateCount > 0"
                type="button"
                class="duplicate-inline-btn"
                @click="openDuplicateDialog"
              >
                중복 {{ duplicateCount.toLocaleString() }}건
              </button>
            </div>
          </div>
          <div v-else class="no-data-message">
            <v-icon size="48" color="grey-lighten-2">mdi-file-document-outline</v-icon>
            <p>해당 조건의 인건비지출내역이 없습니다.</p>
          </div>
        </div>

        <div
          v-if="resultModal.status === 'completed' && resultModal.type !== 'caion_ltcs_labor_cost'"
          class="data-table-section"
        >
          <div class="query-conditions">
            <div class="condition-title">
              <v-icon size="16" class="mr-1">mdi-filter-outline</v-icon>
              조회 조건
            </div>
            <div class="condition-items">
              <div class="condition-item">
                <span class="condition-label">사업구분</span>
                <span class="condition-value">{{ bizGubun || '전체' }}</span>
              </div>
              <div class="condition-item">
                <span class="condition-label">연월</span>
                <span class="condition-value">{{ formatYearMonth(resultModal.yearMonth) || '전체' }}</span>
              </div>
              <div v-if="scheduleSearch" class="condition-item">
                <span class="condition-label">검색어</span>
                <span class="condition-value highlight">{{ scheduleSearch }}</span>
              </div>
            </div>
          </div>

          <div class="summary-cards">
            <div class="summary-card">
              <div class="card-icon"><v-icon color="primary">mdi-calendar-check</v-icon></div>
              <div class="card-content">
                <span class="card-value">{{ scheduleData.summary.totalCount?.toLocaleString() || 0 }}</span>
                <span class="card-label">{{
                  normalizeLtcsType(resultModal.type) === 'caion_ltcs_claim_detail' ? '방문 건수' : '전체 건수'
                }}</span>
              </div>
            </div>
            <div v-if="showCaregiverCount" class="summary-card">
              <div class="card-icon"><v-icon color="success">mdi-account-hard-hat</v-icon></div>
              <div class="card-content">
                <span class="card-value">{{ scheduleData.summary.caregiverCount || 0 }}</span>
                <span class="card-label">종사자</span>
              </div>
            </div>
            <div v-if="showRecipientCount" class="summary-card">
              <div class="card-icon"><v-icon color="secondary">mdi-account-heart</v-icon></div>
              <div class="card-content">
                <span class="card-value">{{ scheduleData.summary.recipientCount || 0 }}</span>
                <span class="card-label">수급자</span>
              </div>
            </div>
            <div v-if="showDateCount" class="summary-card">
              <div class="card-icon"><v-icon color="info">mdi-calendar-range</v-icon></div>
              <div class="card-content">
                <span class="card-value">{{ scheduleData.summary.dateCount || 0 }}일</span>
                <span class="card-label">날짜수</span>
              </div>
            </div>
            <div
              v-if="normalizeLtcsType(resultModal.type) === 'caion_ltcs_claim_detail'"
              class="summary-card"
            >
              <div class="card-icon"><v-icon color="warning">mdi-currency-krw</v-icon></div>
              <div class="card-content">
                <span class="card-value">{{ (scheduleData.summary.totalFee || 0).toLocaleString() }}원</span>
                <span class="card-label">총 수가</span>
              </div>
            </div>
            <div
              v-if="normalizeLtcsType(resultModal.type) === 'caion_ltcs_claim_detail' && (scheduleData.summary.portalRawCount || 0) > (scheduleData.summary.totalCount || 0)"
              class="summary-card summary-card--portal-raw"
            >
              <div class="card-icon"><v-icon color="#1565c0">mdi-file-document-multiple-outline</v-icon></div>
              <div class="card-content">
                <span class="card-value">{{ (scheduleData.summary.portalRawCount || 0).toLocaleString() }}줄</span>
                <span class="card-label">포털 원본</span>
              </div>
            </div>
            <button
              v-if="duplicateCount > 0"
              type="button"
              class="summary-card summary-card--duplicate"
              @click="openDuplicateDialog"
            >
              <div class="card-icon"><v-icon color="error">mdi-content-duplicate</v-icon></div>
              <div class="card-content">
                <span class="card-value duplicate-link">{{ duplicateCount.toLocaleString() }}건</span>
                <span class="card-label">{{
                  normalizeLtcsType(resultModal.type) === 'caion_ltcs_claim_detail'
                    ? '동일 방문 중복 (클릭)'
                    : '중복 데이터 (클릭)'
                }}</span>
              </div>
            </button>
          </div>
          <LtcsClaimDetailPreferNotice
            v-if="normalizeLtcsType(resultModal.type) === 'caion_ltcs_claim_detail'"
            :portal-raw-count="scheduleData.summary.portalRawCount || 0"
            :display-count="scheduleData.summary.totalCount || 0"
            :status-pair-count="scheduleData.summary.statusPairCount || 0"
          />

          <div class="search-area">
            <v-text-field
              v-model="scheduleSearch"
              prepend-inner-icon="mdi-magnify"
              label="이름 검색 (요양보호사/수급자)"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              style="max-width: 300px"
              @keyup.enter="searchScheduleData"
              @click:clear="clearSearch"
            />
            <v-btn variant="flat" class="btn-search" @click="searchScheduleData">검색</v-btn>
            <v-spacer />
            <span class="total-count">
              총 <strong>{{ scheduleData.total.toLocaleString() }}</strong>건
              <template v-if="normalizeLtcsType(resultModal.type) === 'caion_ltcs_claim_detail'"> (방문)</template>
            </span>
          </div>

          <v-data-table
            :headers="scheduleHeaders"
            :items="scheduleData.items"
            :loading="scheduleLoading"
            :items-per-page="itemsPerPage"
            :page="currentPage"
            class="schedule-table"
            density="compact"
            :sort-by="sortBy"
            hide-default-footer
            @update:sort-by="onSortChange"
          >
            <template #item.clsr_wdate="{ item }">{{ formatDateTime(item.clsr_wdate) }}</template>
            <template #item.clsr_rdate="{ item }">{{ formatDateTime(item.clsr_rdate) }}</template>
            <template #item.clsr_service_type="{ item }">
              <v-chip size="x-small" :color="getServiceTypeColor(item.clsr_service_type)">
                {{ item.clsr_service_type || '-' }}
              </v-chip>
            </template>
            <template #item.clci_birth_date="{ item }">{{ formatDateFull(item.clci_birth_date) }}</template>
            <template #item.clci_work_start_date="{ item }">{{ formatDateFull(item.clci_work_start_date) }}</template>
            <template #item.clci_work_end_date="{ item }">{{ formatDateFull(item.clci_work_end_date) }}</template>
            <template #item.clci_wdate="{ item }">{{ formatDateTime(item.clci_wdate) }}</template>
            <template #item.clsp_date="{ item }">{{ formatDateFull(item.clsp_date) }}</template>
            <template #item.clsp_start_time="{ item }">{{ formatTime(item.clsp_start_time) }}</template>
            <template #item.clsp_end_time="{ item }">{{ formatTime(item.clsp_end_time) }}</template>
            <template #item.clsp_is_family="{ item }">{{ formatYn(item.clsp_is_family) }}</template>
            <template #item.clsp_caregiver_birth="{ item }">{{ formatDateFull(item.clsp_caregiver_birth) }}</template>
            <template #item.clsp_service_type="{ item }">
              <v-chip size="x-small" :color="getServiceTypeColor(item.clsp_service_type)">
                {{ item.clsp_service_type || '-' }}
              </v-chip>
            </template>
            <template #item.clc_caregiver_birth="{ item }">{{ formatDateFull(item.clc_caregiver_birth) }}</template>
            <template #item.clc_date="{ item }">{{ formatDateFull(item.clc_date) }}</template>
            <template #item.clc_plan_time="{ item }">
              {{ formatTimeRange(item.clc_plan_start_time, item.clc_plan_end_time) }}
            </template>
            <template #item.clc_work_time="{ item }">
              {{ formatTimeRange(item.clc_work_start_time, item.clc_work_end_time) }}
            </template>
            <template #item.clc_is_family="{ item }">{{ formatYn(item.clc_is_family) }}</template>
            <template #item.clc_service_type="{ item }">
              <v-chip size="x-small" :color="getServiceTypeColor(item.clc_service_type)">
                {{ item.clc_service_type || '-' }}
              </v-chip>
            </template>
            <template #item.clcd_status="{ item }">
              <v-chip size="x-small" :color="getClaimStatusColor(item.clcd_status)">
                {{ item.clcd_status || '-' }}
              </v-chip>
            </template>
            <template #item.clcd_date="{ item }">{{ formatDateFull(item.clcd_date) }}</template>
            <template #item.clcd_service_time="{ item }">
              {{ formatTimeRange(item.clcd_service_start_time, item.clcd_service_end_time) }}
            </template>
            <template #item.clcd_fee="{ item }">{{ formatMoney(item.clcd_fee) }}</template>
            <template #item.clcd_deduction_score="{ item }">{{ item.clcd_deduction_score ?? '-' }}</template>
            <template #item.clcd_deduction_rate="{ item }">{{ formatRate(item.clcd_deduction_rate) }}</template>
            <template #no-data>
              <div class="no-data">
                <v-icon size="40" color="grey-lighten-2">mdi-calendar-blank</v-icon>
                <p>조회된 데이터가 없습니다.</p>
              </div>
            </template>
          </v-data-table>

          <div class="pagination-area">
            <v-select
              v-model="itemsPerPage"
              :items="[20, 50, 100]"
              label="페이지당"
              variant="outlined"
              density="compact"
              hide-details
              style="width: 110px"
              @update:model-value="onItemsPerPageChange"
            />
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="7"
              density="compact"
              @update:model-value="onPageChange"
            />
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <v-btn variant="flat" class="btn-cancel" @click="closeResultModal">닫기</v-btn>
      </div>
    </div>
  </v-dialog>

  <LtcsDuplicateDialog
    v-model="duplicateDialogShow"
    :loading="duplicateLoading"
    :duplicate-count="duplicateCount"
    :duplicate-group-count="duplicateGroupCount"
    :groups="duplicateGroups"
    :table-headers="scheduleHeaders"
    :idx-key="duplicateIdxKey"
    :source-type="normalizeLtcsType(resultModal.type)"
  />

  <LtcsRequestConfirmDialog
    v-model="confirmDialog.show"
    :type-name="confirmDialog.typeName"
    :year-month="confirmDialog.yearMonth"
    :biz-gubun="confirmDialog.bizGubun"
    :facility-name="facilityName"
    :needs-year-month="confirmDialog.needsYearMonth"
    :loading="!!loadingMap[confirmDialog.type]"
    @confirm="confirmSubmitRequest"
  />

  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
    {{ snackbar.message }}
  </v-snackbar>
</template>

<script setup lang="ts">
import { computed, watch, inject, ref, type Ref } from 'vue';
import { useStore } from 'vuex';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import LtcsDuplicateDialog from '@/components/member/request/LtcsDuplicateDialog.vue';
import LtcsClaimDetailPreferNotice from '@/components/member/request/LtcsClaimDetailPreferNotice.vue';
import LtcsRequestConfirmDialog from '@/components/member/request/LtcsRequestConfirmDialog.vue';
import {
  useLtcsMacroRequest,
  HR_LTCS_REQUEST_TYPES,
  LTCS_TYPE_LABELS,
  normalizeLtcsType,
} from '@/composables/useLtcsMacroRequest';

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    memberCode?: string;
    bizGubun?: string;
    yearMonth?: string;
    allowedTypes?: readonly string[];
  }>(),
  {
    allowedTypes: () => HR_LTCS_REQUEST_TYPES,
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'updated'): void;
}>();

const store = useStore();
const isAdmin = computed(
  () => Number(store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0) >= 60
);

const injectedMemberData = inject<Ref<any>>('memberData', ref(null));
const injectedBizGubun = inject<Ref<string>>('selectedBizGubun', ref(''));

const memberCodeRef = computed(() =>
  props.memberCode || injectedMemberData?.value?.m_code || ''
);
const bizGubunRef = computed(() =>
  props.bizGubun || injectedBizGubun?.value || ''
);
const toYearMonth = (raw: string) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length < 6) return '';
  const ym = digits.slice(0, 6);
  const month = Number(ym.slice(4, 6));
  if (month < 1 || month > 12) return '';
  return ym;
};

const currentYearMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const selectedYearMonth = ref(toYearMonth(props.yearMonth) || currentYearMonth());

const yearMonthInputValue = computed(() => {
  const ym = selectedYearMonth.value;
  if (ym.length !== 6) return '';
  return `${ym.slice(0, 4)}-${ym.slice(4, 6)}`;
});

const shiftYearMonth = (delta: number) => {
  const base = toYearMonth(selectedYearMonth.value) || currentYearMonth();
  const year = Number(base.slice(0, 4));
  const month = Number(base.slice(4, 6));
  const next = new Date(year, month - 1 + delta, 1);
  selectedYearMonth.value = `${next.getFullYear()}${String(next.getMonth() + 1).padStart(2, '0')}`;
};

const onYearMonthInput = (event: Event) => {
  const next = toYearMonth((event.target as HTMLInputElement).value);
  if (next) selectedYearMonth.value = next;
};

const dialog = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const {
  requestTypes,
  filteredHistoryItems,
  historyLoading,
  loadingMap,
  hasActiveRequest,
  snackbar,
  resultModal,
  scheduleLoading,
  scheduleSearch,
  scheduleData,
  duplicateDialogShow,
  duplicateLoading,
  duplicateGroups,
  duplicateCount,
  duplicateGroupCount,
  duplicateIdxKey,
  currentPage,
  itemsPerPage,
  sortBy,
  totalPages,
  scheduleHeaders,
  showCaregiverCount,
  showRecipientCount,
  showDateCount,
  getTypeLabel,
  getLatestProgressMessage,
  getStatusLabel,
  getParamsLabel,
  getLatestRequestForType,
  formatDateTime,
  formatYearMonth,
  formatNumber,
  formatDateFull,
  formatTime,
  formatTimeRange,
  formatMoney,
  formatRate,
  formatYn,
  getServiceTypeColor,
  getClaimStatusColor,
  confirmDialog,
  submitRequest,
  confirmSubmitRequest,
  cancelRequest,
  showResultModal,
  closeResultModal,
  openDuplicateDialog,
  searchScheduleData,
  clearSearch,
  onSortChange,
  onPageChange,
  onItemsPerPageChange,
  loadHistory,
  formatProgressMessage,
} = useLtcsMacroRequest({
  memberCode: memberCodeRef,
  bizGubun: bizGubunRef,
  selectedYearMonth,
  allowedTypes: props.allowedTypes,
  verboseErrors: isAdmin,
});

const bizGubun = bizGubunRef;
const facilityName = computed(
  () => injectedMemberData?.value?.m_name || injectedMemberData?.value?.m_facility_name || ''
);

const closeModal = () => {
  dialog.value = false;
};

const getCardStatus = (type: string) => {
  const normalized = normalizeLtcsType(type);
  const needsYm = LTCS_TYPE_LABELS[normalized]?.needsYearMonth ?? false;
  const ym = needsYm ? selectedYearMonth.value : undefined;
  const latest = getLatestRequestForType(type, ym);
  if (!latest) return null;

  if (latest.cmq_status === 'pending' || latest.cmq_status === 'processing') {
    const progress = formatProgressMessage(getLatestProgressMessage(latest) || '');
    return {
      label: progress || getStatusLabel(latest.cmq_status),
      color: 'processing',
    };
  }
  if (latest.cmq_status === 'completed') {
    return { label: '최근 요청 완료', color: 'ready' };
  }
  if (latest.cmq_status === 'failed') {
    return { label: '최근 요청 실패', color: 'failed' };
  }
  return null;
};

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      selectedYearMonth.value = toYearMonth(props.yearMonth) || currentYearMonth();
      if (memberCodeRef.value) loadHistory();
    }
  }
);

watch(hasActiveRequest, (active, wasActive) => {
  if (wasActive && !active) {
    emit('updated');
  }
});

watch(filteredHistoryItems, () => {
  if (props.modelValue) {
    emit('updated');
  }
});
</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/pages/ltcs-hr-request-modal';
</style>
